import { readLinkedValues } from "./multiValueExport";

const extractCode = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.includes(" - ")) {
    return trimmed.split(" - ")[0].trim();
  }
  return trimmed;
};

const normalizeKey = (value: string): string => value.trim().toLowerCase();

/** Matches import values to autocomplete options (`CODE` or `CODE - Name`). */
export const isAllowedAutocompleteValue = (
  value: unknown,
  options: string[],
): boolean => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return false;
  }

  const importCode = extractCode(raw);
  const importKey = normalizeKey(raw);
  const importCodeKey = normalizeKey(importCode);

  return options.some((option) => {
    const optionKey = normalizeKey(option);
    const optionCodeKey = normalizeKey(extractCode(option));
    return (
      optionKey === importKey ||
      optionCodeKey === importCodeKey ||
      optionCodeKey === importKey
    );
  });
};

export type RoomDefinitionImportValidationResult = {
  valid: boolean;
  message?: string;
  rowErrors: string[];
};

/** Ensures every imported row uses room type / building codes defined in onboarding. */
export const validateRoomDefinitionImportAgainstMasterData = (
  rows: Record<string, unknown>[],
  roomTypeOptions: string[],
  buildingOptions: string[],
): RoomDefinitionImportValidationResult => {
  const rowErrors: string[] = [];

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const roomTypeCode = row.roomTypeCode;
    if (!isAllowedAutocompleteValue(roomTypeCode, roomTypeOptions)) {
      rowErrors.push(
        `Row ${rowNumber}: roomTypeCode "${String(roomTypeCode ?? "").trim()}" is not defined in Room Type Maintenance.`,
      );
    }

    const buildings = readLinkedValues(row.buildingCode);
    if (buildings.length === 0) {
      rowErrors.push(`Row ${rowNumber}: buildingCode is required.`);
    } else {
      for (const building of buildings) {
        if (!isAllowedAutocompleteValue(building, buildingOptions)) {
          rowErrors.push(
            `Row ${rowNumber}: buildingCode "${building}" is not defined in Building Codes.`,
          );
        }
      }
    }
  });

  if (rowErrors.length > 0) {
    return {
      valid: false,
      message:
        "Please enter an existing roomTypeCode or buildingCode. Import was not applied.",
      rowErrors,
    };
  }

  return { valid: true, rowErrors: [] };
};
